import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { User, Mail, Phone, Briefcase, FileText, MessageCircle } from "lucide-react";
// Import the service manager
import InvestorEnquiryManager from "../../../services/home/InvestorEnquiryManager"; // Adjust path as necessary

const InvestorFormSection = () => {
    const { register, handleSubmit, reset } = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null); // 'success' or 'error'

    const onSubmit = async (data) => {
        setIsLoading(true);
        setSubmissionStatus(null);
        
        // 1. Separate the file object from the rest of the form data
        const file = data.file[0]; // react-hook-form gives a FileList, take the first item
        
        // 2. Prepare the DTO payload
        const enquiryDTO = {
            fullName: data.fullName,
            email: data.email,
            phone: data.phone,
            company: data.company,
            designation: data.designation,
            interests: data.interests || [],
            otherInterest: data.otherInterest,
            preferredInvestmentRange: data.range || [], 
            engagementType: data.engagement,
            message: data.message,
        };

        try {
            // 3. Call the frontend service manager's create method
            const response = await InvestorEnquiryManager.createEnquiry(enquiryDTO, file);

            // 💡 FIX APPLIED HERE: Check the 'responseCode' field from the API response body
            if (response && response.responseCode === 200 && response.responseData) {
                setSubmissionStatus('success');
                alert("Form submitted successfully! Our Investor Relations Team will contact you shortly.");
                reset(); // Clear the form on success
            } else {
                // This block handles cases where the HTTP request succeeded (e.g., status 200) 
                // but the API responseCode indicates a business error (e.g., 400 or a custom code)
                setSubmissionStatus('error');
                console.error("Submission failed:", response.responseMessage || "Unknown API error");
                alert(`Submission failed: ${response.responseMessage || 'Please check your input and try again.'}`);
            }
        } catch (error) {
            // Handle network errors (e.g., server unreachable, axios non-2xx status codes)
            setSubmissionStatus('error');
            // Use error.response.data for detailed error messages if available
            const errorMsg = error.response?.data?.responseMessage || error.message;
            console.error("Network or system error:", errorMsg);
            alert(`A system error occurred. Please try again later. Details: ${errorMsg}`);
        } finally {
            setIsLoading(false);
        }
    };


  const InputField = ({ icon: Icon, placeholder, ...rest }) => (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
      <input
        placeholder={placeholder}
        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white text-sm shadow-sm transition"
        {...rest}
      />
    </div>
  );

  return (
    <section id="investor-form" className="bg-gradient-to-br from-slate-50 to-white py-20 px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <motion.h2
          className="text-4xl font-bold text-center text-slate-900 mb-4"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Investor Enquiry Form
        </motion.h2>
        <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
          Fill out the form below. Our Investor Relations Team will connect with you shortly.
        </p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white p-8 md:p-10 rounded-3xl shadow-2xl grid gap-8"
        >
          {/* ... (Your existing form structure) ... */}
          {/* Section: Your Details */}
          <div>
            <h3 className="text-2xl font-semibold text-slate-800 mb-4">Your Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <InputField icon={User} placeholder="Full Name *" {...register("fullName", { required: true })} />
              <InputField icon={Mail} placeholder="Email Address *" {...register("email", { required: true })} />
              <InputField icon={Phone} placeholder="Phone Number *" {...register("phone", { required: true })} />
              <InputField icon={Briefcase} placeholder="Company / Organization" {...register("company")} />
              <InputField icon={Briefcase} placeholder="Designation / Role" {...register("designation")} />
            </div>
          </div>

          {/* Section: Investment Preferences */}
          <div>
            <h3 className="text-2xl font-semibold text-slate-800 mb-4"> Investment Preferences</h3>
            <div className="grid gap-3">
              <label className="font-medium text-gray-700">I am interested in:</label>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  "Equity Investment",
                  "Revenue Sharing",
                  "Convertible Notes / SAFE",
                  "Logistics / Distribution Partnership",
                  "Publishing / EdTech Collaboration",
                  "Fixed Deposit Investment (Below ₹2 Lakhs)",
                ].map((label, idx) => (
                  <label key={idx} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                    <input type="checkbox" value={label} {...register("interests")} />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
                <input
                  type="text"
                  placeholder="Other..."
                  {...register("otherInterest")}
                  className="input-style md:col-span-2 border border-gray-300 rounded-lg px-4 py-2"
                />
              </div>
            </div>
          </div>

          {/* Investment Range */}
          <div>
            <label className="font-medium text-gray-700 block mb-2">Preferred Investment Range:</label>
            <div className="grid md:grid-cols-3 gap-3">
              {["₹50K – ₹2L", "₹2L – ₹10L", "₹10L – ₹50L", "₹50L – ₹1 Cr", "₹1 Cr+"].map((range, idx) => (
                <label key={idx} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                  <input type="checkbox" value={range} {...register("range")} />
                  <span className="text-sm text-gray-700">{range}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Engagement Type */}
          <div>
            <label className="font-medium text-gray-700 block mb-2">Engagement Type:</label>
            <div className="grid md:grid-cols-2 gap-3">
              {["Passive Investor", "Strategic Advisor", "Joint Venture / Collaboration", "Open to Explore"].map(
                (type, idx) => (
                  <label key={idx} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                    <input type="radio" value={type} {...register("engagement")} name="engagement" />
                    <span className="text-sm text-gray-700">{type}</span>
                  </label>
                )
              )}
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="font-medium text-gray-700 block mb-2">
              <FileText className="inline-block mr-1" size={16} />
              Upload Proposal or Profile (Optional)
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx"
              {...register("file")}
              className="w-full text-sm file:bg-slate-200 file:border-none file:rounded-md file:py-2 file:px-4 file:text-gray-800"
            />
          </div>

          {/* Message */}
          <div>
            <label className="font-medium text-gray-700 block mb-2">
              <MessageCircle className="inline-block mr-1" size={16} />
              Message or Query
            </label>
            <textarea
              rows="4"
              placeholder="Write your message here..."
              {...register("message")}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Submit */}
          <div className="text-center mt-6">
            <motion.button
              type="submit"
              whileHover={{ scale: 1.05 }}
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold shadow-md hover:bg-primary/80 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Submit Enquiry'}
            </motion.button>
          </div>
          
          {/* Submission Status Feedback */}
                    {submissionStatus === 'success' && (
                        <p className="text-center text-green-600 mt-4 font-semibold">
                            ✅ Enquiry submitted successfully! Thank you.
                        </p>
                    )}
                    {submissionStatus === 'error' && (
                        <p className="text-center text-red-600 mt-4 font-semibold">
                            ❌ Submission failed. Please try again.
                        </p>
                    )}
        </form>
      </div>
    </section>
  );
};

export default InvestorFormSection;